import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import { queryKeys } from '@/constants/query-key';
import { ApiNamePaths } from '@/constants/rest-api-name-path';
import StorageKeys from '@/constants/storageKeys';
import { apiGet, useGenericQuery } from '@/modules/hooks/use-query';
import { settingState } from '@/state/settingState';
import type { CurrentUser } from '@/types/auth';
import type { DriverDriversRes } from '@/types/driver-drivers';
import { ServiceType } from '@/types/serviceType';

export type DriverDataResult = {
  success: boolean;
  driverData?: DriverDriversRes['driverData'];
  error?: string;
};

// useGenericQueryを使用したドライバーデータ取得フック（v2 Backend API）
export const useDriverDataQuery = (driverId: string | undefined) =>
  useGenericQuery<DriverDriversRes>(
    queryKeys.v2DriverData,
    ApiNamePaths.driverDriversApi.apiName,
    `${ApiNamePaths.driverDriversApi.apiPath}/${driverId}`,
    { enabled: !!driverId },
  );

export const useDriverData = () => {
  const { t } = useTranslation();
  const [, setSetting] = useRecoilState(settingState);

  const getDriverData = useCallback(
    async (driverId: string): Promise<DriverDataResult> => {
      try {
        const response = await apiGet<DriverDriversRes>(
          ApiNamePaths.driverDriversApi.apiName,
          `${ApiNamePaths.driverDriversApi.apiPath}/${driverId}`,
        );

        const data = response;
        const driverData = data?.driverData;

        if (!driverData) {
          return { success: false, error: 'ドライバーデータが見つかりません' };
        }

        return {
          success: true,
          driverData,
        };
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : t('login.unexpectedError');

        return { success: false, error: errorMessage };
      }
    },
    [t],
  );

  const updateSettingState = useCallback(
    (userInfo: CurrentUser, driverData: DriverDriversRes['driverData']) => {
      // 設定状態を更新
      setSetting((setting) => ({
        ...(setting || {}),
        driverId: userInfo.userId,
        loginId: userInfo.username,
        driverManageCompanyId: driverData.driverManageCompanyId,
        serviceType: driverData.serviceType,
        areaList: driverData.areaList,
      }));

      // PreBookingサービスの場合、車両IDをセッションストレージに保存
      if (driverData.serviceType === ServiceType.PreBooking && driverData.vehicleId) {
        sessionStorage.setItem(StorageKeys.VEHICLE_ID, driverData.vehicleId);
      }
    },
    [setSetting],
  );

  return {
    getDriverData,
    updateSettingState,
  };
};
